import { useEffect, useState } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiMessageCircle, FiSend, FiCheckCircle, FiXCircle, FiTrash2 } from 'react-icons/fi'

export default function ContactChatPanel({ websiteId, selectedCustomer }) {
  const [conversations, setConversations] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedConversationId, setSelectedConversationId] = useState(null)
  const [messageText, setMessageText] = useState('')
  const [selectedFiles, setSelectedFiles] = useState([])
  const [sending, setSending] = useState(false)
  const [closing, setClosing] = useState(false)

  const selectedCustomerId = selectedCustomer?.customer?._id || selectedCustomer?._id || null

  const fetchConversations = async () => {
    if (!websiteId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const response = await axios.get(`/contact-messages/conversations/website/${websiteId}`, {
        params: selectedCustomerId ? { customerId: selectedCustomerId } : {}
      })
      const nextConversations = response.data.data || []
      setConversations(nextConversations)
      if (!selectedConversationId && nextConversations.length > 0) {
        setSelectedConversationId(nextConversations[0]._id)
      }
    } catch (error) {
      console.error('[ContactChatPanel] Error loading conversations:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setSelectedConversationId(null)
    fetchConversations()
  }, [websiteId, selectedCustomerId])

  const selectedConversation = conversations.find((conversation) => conversation._id === selectedConversationId) || null

  const handleSendMessage = async (event) => {
    event.preventDefault()
    if (!selectedConversation || (!messageText.trim() && selectedFiles.length === 0)) return

    try {
      setSending(true)
      const formData = new FormData()
      formData.append('message', messageText.trim())
      selectedFiles.forEach((file) => formData.append('attachments', file))

      const response = await axios.post(`/contact-messages/conversations/${selectedConversation._id}/messages`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      const updatedConversation = response.data.data
      setConversations((prev) => prev.map((conversation) => conversation._id === updatedConversation._id ? updatedConversation : conversation))
      setMessageText('')
      setSelectedFiles([])
    } catch (error) {
      console.error('[ContactChatPanel] Error sending message:', error)
    } finally {
      setSending(false)
    }
  }

  const handleToggleConversationStatus = async (nextStatus) => {
    if (!selectedConversation) return
    try {
      setClosing(true)
      const response = await axios.post(nextStatus === 'closed' ? `/contact-messages/${selectedConversation._id}/close` : `/contact-messages/${selectedConversation._id}/reopen`)
      const updatedConversation = response.data.data
      setConversations((prev) => prev.map((conversation) => conversation._id === updatedConversation._id ? updatedConversation : conversation))
      toast.success(nextStatus === 'closed' ? 'Conversation closed successfully' : 'Conversation reopened successfully')
    } catch (error) {
      console.error('[ContactChatPanel] Error updating conversation status:', error)
      toast.error(error.response?.data?.message || 'Unable to update this conversation right now')
    } finally {
      setClosing(false)
    }
  }

  const handleDeleteConversation = async () => {
    if (!selectedConversation || !window.confirm('Delete this conversation?')) return
    try {
      await axios.delete(`/contact-messages/${selectedConversation._id}`)
      setConversations((prev) => prev.filter((conversation) => conversation._id !== selectedConversation._id))
      setSelectedConversationId(null)
    } catch (error) {
      console.error('[ContactChatPanel] Error deleting conversation:', error)
    }
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">Contact Us Message Chat</h3>
          <p className="text-sm text-gray-500">Keep customer conversations in one place.</p>
        </div>
        <button type="button" onClick={fetchConversations} className="self-start text-sm text-blue-600 hover:text-blue-700">Refresh</button>
      </div>

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-500">Loading conversations...</div>
      ) : conversations.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-center text-sm text-gray-600">No conversations have been started yet.</div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-[240px_minmax(0,1fr)]">
          <div className="space-y-2">
            {conversations.map((conversation) => (
              <button
                key={conversation._id}
                type="button"
                onClick={() => setSelectedConversationId(conversation._id)}
                className={`w-full rounded-lg border p-3 text-left transition ${selectedConversationId === conversation._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'}`}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <FiMessageCircle className="text-blue-600" />
                  {conversation.subject || 'Contact request'}
                </div>
                <p className="mt-1 text-xs text-gray-500">{conversation.lastMessagePreview || conversation.message}</p>
              </button>
            ))}
          </div>

          {selectedConversation && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <h4 className="text-base font-semibold text-gray-900">{selectedConversation.subject || 'Contact request'}</h4>
                  <p className="text-sm text-gray-500">{selectedConversation.customerName || selectedConversation.visitorName || 'Customer'}</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${selectedConversation.conversationStatus === 'closed' ? 'bg-gray-100 text-gray-700' : 'bg-green-100 text-green-700'}`}>
                    {selectedConversation.conversationStatus === 'closed' ? <FiXCircle /> : <FiCheckCircle />}
                    {selectedConversation.conversationStatus === 'closed' ? 'Closed' : 'Open'}
                  </span>
                  <button type="button" onClick={() => handleToggleConversationStatus(selectedConversation.conversationStatus === 'closed' ? 'open' : 'closed')} disabled={closing} className="rounded-lg border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    {selectedConversation.conversationStatus === 'closed' ? 'Reopen' : 'Close'}
                  </button>
                  <button type="button" onClick={handleDeleteConversation} className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700 hover:bg-red-50"><FiTrash2 className="inline" /></button>
                </div>
              </div>

              <div className="space-y-3">
                {(selectedConversation.messages || []).map((message) => (
                  <div key={message._id} className={`rounded-lg border p-3 ${message.sender === 'owner' ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                    <div className="mb-1 flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      <span>{message.senderName || (message.sender === 'owner' ? 'You' : 'Customer')}</span>
                      <span>{new Date(message.createdAt).toLocaleString()}</span>
                    </div>
                    <p className="whitespace-pre-wrap text-sm text-gray-700">{message.text}</p>
                    {(message.attachments || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(message.attachments || []).map((attachment, index) => (
                          <a key={`${message._id}-${index}`} href={attachment.url} target="_blank" rel="noreferrer" className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-blue-700 hover:bg-gray-50">
                            {attachment.filename || attachment.type || 'Attachment'}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <form onSubmit={handleSendMessage} className="mt-4 space-y-3 border-t border-gray-200 pt-4">
                <label className="block text-sm font-medium text-gray-700">Reply</label>
                {selectedConversation.conversationStatus === 'closed' && (
                  <div className="rounded-lg bg-gray-100 border border-gray-300 p-3 text-sm text-gray-700">
                    <p className="font-semibold mb-1">This conversation is closed</p>
                    <p>To send messages, please reopen the conversation first.</p>
                  </div>
                )}
                <textarea
                  value={messageText}
                  onChange={(event) => setMessageText(event.target.value)}
                  rows={4}
                  disabled={selectedConversation.conversationStatus === 'closed'}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-500"
                  placeholder="Write a reply to the customer..."
                />
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={(event) => setSelectedFiles(Array.from(event.target.files || []))}
                  disabled={selectedConversation.conversationStatus === 'closed'}
                  className="block w-full text-sm text-gray-500 disabled:cursor-not-allowed disabled:opacity-50"
                />
                {selectedFiles.length > 0 && (
                  <p className="text-xs text-gray-500">Selected attachments: {selectedFiles.map((file) => file.name).join(', ')}</p>
                )}
                <div className="flex justify-end">
                  <button type="submit" disabled={sending || (!messageText.trim() && selectedFiles.length === 0) || selectedConversation.conversationStatus === 'closed'} className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed">
                    <FiSend />
                    {sending ? 'Sending...' : 'Send reply'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
